/** @file The serial send queue: one batch in flight; an answer sets its commit rev and remaps later ids. */
import { throwError } from '@pose2d/format/throwError';
import type { CommandCall, Id } from '@pose2d/format/types';
import { docStore } from '../../stores/docStore';
import { liveStore, type PendingBatch } from '../../stores/liveStore';
import type { ApplyResponse } from '../apiTypes';
import { liveSession, type SyncSession } from './liveSession';
import { noteOwnRev } from './noteOwnRev';
import { pushNotice } from './pushNotice';
import { remapIds } from './remapIds';
import { remapSelection } from './remapSelection';
import { sessionBatches } from './sessionBatches';

const callsOf = (batch: PendingBatch): CommandCall[] => batch.pending.map(({ name, input }) => ({ name, input }));

/** Writes the batches and mirrors their commands into DocSlice.pending. */
function setBatches(batches: PendingBatch[]): void {
  liveStore.setState({ batches });
  const { slice } = docStore.getState();
  if (slice !== null) docStore.setState({ slice: { ...slice, pending: batches.flatMap((b) => b.pending) } });
}

/** clientId → serverId of what batch `seq` created; the batch's own entry is dropped. */
function takeMap(session: SyncSession, seq: number, res: ApplyResponse): ReadonlyMap<Id, Id> {
  const map = remapIds.between(session.created.get(seq) ?? {}, res.results);
  session.created.delete(seq);
  return map;
}

/** The answer: committedRev set (retired at once when already confirmed); the batch itself, later batches and the
 *  selection remapped together, so the replayed view holds the server ids the selection now names (the selection
 *  prune would drop them otherwise, fx1 T-464). */
function accepted(session: SyncSession, seq: number, res: ApplyResponse): void {
  const map = takeMap(session, seq, res);
  const rev = docStore.getState().slice?.rev ?? 0;
  const next = liveStore
    .getState()
    .batches.map((b) => (b.seq === seq ? { ...b, committedRev: res.rev, pending: remapIds.apply(b.pending, map) } : b.seq > seq ? { ...b, pending: remapIds.apply(b.pending, map) } : b))
    .filter((b) => b.committedRev === null || b.committedRev > rev);
  setBatches(next);
  remapSelection(map);
  noteOwnRev(session, res.rev);
}

/** A refusal: the batch leaves the view and becomes a notice whose Retry re-dispatches its commands. */
function refused(session: SyncSession, seq: number, thrown: unknown): void {
  const batches = liveStore.getState().batches;
  const batch = batches.find((b) => b.seq === seq);
  session.created.delete(seq);
  setBatches(batches.filter((b) => b.seq !== seq));
  pushNotice(batch === undefined ? null : callsOf(batch), thrown);
}

/** After stop (finding 16) only the batches left to send change: the answered one leaves, later ones are remapped. */
function settledAfterStop(session: SyncSession, seq: number, res: ApplyResponse | null): void {
  const map = res === null ? new Map<Id, Id>() : takeMap(session, seq, res);
  session.created.delete(seq);
  session.afterStop = (session.afterStop ?? []).filter((b) => b.seq !== seq).map((b) => (b.seq > seq ? { ...b, pending: remapIds.apply(b.pending, map) } : b));
}

/** Sends batch `seq`, also after the editor left the project (its store writes are dropped then); refused only when
 *  the batch is gone or the same project was opened again by a newer session (its view never showed the batch). */
async function post(session: SyncSession, seq: number): Promise<ApplyResponse> {
  const live = liveSession.get();
  const batch = sessionBatches(session).find((b) => b.seq === seq);
  if (batch === undefined || (live !== session && live?.projectId === session.projectId)) {
    return throwError('NOT_FOUND', { kind: 'batch', ref: String(seq) }, { hint: 'the project was reloaded before the batch was sent' });
  }
  if (live === session) setBatches(liveStore.getState().batches.map((b) => (b.seq === seq ? { ...b, sent: true } : b)));
  try {
    const res = await session.client.applyCommands(session.projectId, callsOf(batch));
    if (liveSession.get() === session) accepted(session, seq, res);
    else settledAfterStop(session, seq, res);
    return res;
  } catch (thrown) {
    if (liveSession.get() === session) refused(session, seq, thrown);
    else settledAfterStop(session, seq, null);
    throw thrown;
  }
}

/** Queues batch `seq` behind every earlier batch of the session; settles with the server's answer. */
export function sendBatch(session: SyncSession, seq: number): Promise<ApplyResponse> {
  const run = session.queue.then(() => post(session, seq));
  session.queue = run.then(
    () => undefined,
    () => undefined,
  );
  return run;
}
